import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, ArrowLeft, Plus, DollarSign, Clock, FileText, CheckCircle, Sparkles, Upload, Download, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VisaDetailPage = () => {
  const { countryId, visaType } = useParams();
  const navigate = useNavigate();
  const [visa, setVisa] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [documentProgress, setDocumentProgress] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [targetDate, setTargetDate] = useState("");

  useEffect(() => {
    loadData();
  }, [countryId, visaType]);

  const loadData = async () => {
    try {
      const [visaData, userData] = await Promise.all([api.getVisa(countryId, visaType), api.getMe().catch(() => null)]);
      setVisa(visaData);
      setUser(userData);
      if (userData) await loadDocumentProgress();
    } catch (error) { navigate("/dashboard"); }
    finally { setIsLoading(false); }
  };

  const loadDocumentProgress = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visa-documents/${countryId}/${visaType}`, { credentials: "include" });
      if (res.ok) setDocumentProgress(await res.json());
    } catch (error) { }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("doc_type", selectedDocType);
    formData.append("doc_name", uploadName);
    try {
      const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/upload`, { method: "POST", credentials: "include", body: formData });
      if (!uploadRes.ok) throw new Error("Upload failed");
      const uploadResult = await uploadRes.json();
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visa-documents/${countryId}/${visaType}/assign`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ doc_type: selectedDocType, doc_id: uploadResult.doc_id }) });
      toast.success("Document uploaded!");
      setUploadDialogOpen(false);
      await loadDocumentProgress();
    } catch (error) { toast.error(error.message); }
  };

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!visa) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b p-4"><div className="max-w-7xl mx-auto flex items-center justify-between"><div className="flex items-center gap-4"><Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft /></Button><div className="flex items-center gap-2"><Globe className="h-6 w-6 text-primary" /><span className="font-bold text-lg">Nomad Roadmap</span></div></div><div className="flex items-center gap-2"><Button variant="outline" onClick={() => setReminderDialogOpen(true)}><Bell className="h-4 w-4 mr-1" />Reminders</Button><Link to="/dashboard"><Button size="sm">Dashboard</Button></Link></div></div></header>
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="mb-8 animate-fade-in"><div className="flex items-center gap-3 mb-4"><span className="text-4xl">{visa.flag_emoji}</span><div><p className="text-muted-foreground">{visa.country_name}</p><h1 className="text-2xl md:text-3xl font-bold font-['Manrope']">{visa.name}</h1></div></div><p className="text-muted-foreground text-lg mb-4">{visa.description}</p></div>
        <Card className="border-border/50"><CardHeader><CardTitle className="text-lg font-['Manrope']">Application Steps & Documents</CardTitle></CardHeader><CardContent><div className="space-y-4">{visa.application_steps?.map((step) => (<div key={step.step} className="rounded-xl border-2 p-4 bg-muted/30"><div className="flex items-start gap-4"><div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">{step.step}</div><div><h4 className="font-semibold">{step.title}</h4><p className="text-sm text-muted-foreground">{step.notes}</p></div></div></div>))}</div></CardContent></Card>
      </main>
    </div>
  );
};

export default VisaDetailPage;